import {useEffect, useState, useCallback} from "react";
import {
  FileText,
  Eye,
  RefreshCw,
  AlertCircle,
  Inbox,
  ClipboardList,
  Clock,
} from "lucide-react";
import OrbitLoader from "../../../ui/components/OrbitLoader";
import MY_CASE_MOCK from "../__mocks__/myCaseResponse";

// DHW brand palette (see project requirements doc). Marigold is an accent
// only, used sparingly for the hours progress, never for large fills.
const DHW_BLUE = "#001489";
const DHW_JADE = "#007864";
const DHW_MARIGOLD = "#FF9425";

// --- Data source -----------------------------------------------------------
// Mock only for now. A backend instance is building POST /my-case in
// parallel. When it is ready, replace the body of loadMyCase with the real
// call below (this is the one-line swap) — the response shape is identical,
// so nothing else in this file changes:
//
//   const {result, data} = await apiFetch("/my-case", {
//     method: "POST",
//     body: JSON.stringify({customer_id, session_id}),
//   });
//   return {result, data};
//
async function loadMyCase() {
  // Simulate a short network round-trip so the loading state is real.
  await new Promise((resolve) => setTimeout(resolve, 600));
  return MY_CASE_MOCK;
}

// Map a free-text status to a calm color tone. Positive/active states use
// Jade; waiting states use the Marigold accent. Everything else falls back to
// a readable grey so an unexpected status from the backend still renders.
const toneForStatus = (status) => {
  const s = (status || "").toLowerCase();
  if (
    ["active", "engaged", "completed", "approved"].some((w) => s.includes(w))
  ) {
    return {bg: "#E6F2EF", text: "#005B4C", dot: DHW_JADE};
  }
  if (["pending", "due", "in progress", "not yet"].some((w) => s.includes(w))) {
    return {bg: "#FFF3E3", text: "#9A5300", dot: DHW_MARIGOLD};
  }
  return {bg: "#EEF0F6", text: "#2B2F3A", dot: "#5B6172"};
};

const StatusPill = ({status}) => {
  const tone = toneForStatus(status);
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold"
      style={{backgroundColor: tone.bg, color: tone.text}}
    >
      <span
        aria-hidden="true"
        className="h-2 w-2 rounded-full"
        style={{backgroundColor: tone.dot}}
      />
      {status || "Unknown"}
    </span>
  );
};

// Hours completed this month, shown against the target for THIS case. The
// target comes from the case data (it varies per participant — it is not
// always 80), so nothing here is hardcoded to a fixed requirement.
const HoursProgress = ({completed, required}) => {
  const safeRequired = Number(required) > 0 ? Number(required) : 0;
  const safeCompleted = Math.max(0, Number(completed) || 0);
  const hasTarget = safeRequired > 0;
  const pct = hasTarget
    ? Math.min(100, Math.round((safeCompleted / safeRequired) * 100))
    : 0;
  const remaining = Math.max(0, safeRequired - safeCompleted);
  return (
    <div
      className="rounded-2xl border border-slate-100 bg-slate-50 p-5"
      role="group"
      aria-label="Hours this month"
    >
      <div className="mb-3 flex items-end justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-500">
            Hours this month
          </p>
          <p className="text-2xl font-bold text-[#191919]">
            {safeCompleted}{" "}
            <span className="text-base font-semibold text-slate-500">
              {hasTarget ? `of ${safeRequired} hours` : "hours"}
            </span>
          </p>
        </div>
        {hasTarget && (
          <span className="text-2xl font-bold" style={{color: DHW_BLUE}}>
            {pct}%
          </span>
        )}
      </div>
      {hasTarget && (
        <div
          className="h-3 w-full overflow-hidden rounded-full bg-slate-200"
          role="progressbar"
          aria-valuenow={safeCompleted}
          aria-valuemin={0}
          aria-valuemax={safeRequired}
          aria-label={`${safeCompleted} of ${safeRequired} hours completed this month`}
        >
          <div
            className="h-full rounded-full transition-all"
            style={{width: `${pct}%`, backgroundColor: DHW_MARIGOLD}}
          />
        </div>
      )}
      <p className="mt-3 text-sm text-slate-600">
        {!hasTarget
          ? "Your case manager will set your hours target."
          : remaining > 0
            ? `You have ${remaining} hours left to report this month.`
            : "You have met your hours for this month. Nice work."}
      </p>
    </div>
  );
};

// The work plan: the activities your case manager has assigned. Renders a
// friendly empty line when the array is missing or empty.
const WorkPlan = ({items}) => {
  const list = Array.isArray(items) ? items : [];
  return (
    <div>
      <h4 className="mb-3 flex items-center gap-2 text-base font-bold text-[#191919]">
        <ClipboardList size={18} style={{color: DHW_BLUE}} />
        Your work plan
      </h4>
      {list.length === 0 ? (
        <p className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
          No activities have been added yet. Your case manager will add these.
        </p>
      ) : (
        <ul className="space-y-2">
          {list.map((item, i) => (
            <li
              key={`${item.name}-${i}`}
              className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-4 py-3"
            >
              <span className="text-sm font-semibold text-[#191919]">
                {item.name || "Untitled"}
              </span>
              <StatusPill status={item.status} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const ReferralCard = ({referral, index}) => {
  const program = referral.benefit_program_code || "Program";
  return (
    <article className="rounded-[2rem] border border-[#ECECEC] bg-white p-5 shadow-sm md:p-7">
      {/* Card header: program + type, and the case number */}
      <div className="mb-5 flex flex-col gap-3 border-b border-slate-100 pb-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white"
            style={{backgroundColor: DHW_BLUE}}
            aria-hidden="true"
          >
            <FileText size={22} />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span
                className="rounded-full px-2.5 py-0.5 text-xs font-black uppercase tracking-wide text-white"
                style={{backgroundColor: DHW_JADE}}
              >
                {program}
              </span>
              <h3 className="text-lg font-bold text-[#191919]">
                {referral.referral_type || "Referral"}
              </h3>
            </div>
            <p className="mt-0.5 text-sm text-slate-500">
              Case number: {referral.case_number || "Not available"}
            </p>
          </div>
        </div>
        <span className="text-xs font-semibold text-slate-400 sm:text-right">
          Referral {index + 1}
        </span>
      </div>

      {/* Hours this month (with this case's target) */}
      <div className="mb-6">
        <HoursProgress
          completed={referral.monthly_hours_completed}
          required={referral.monthly_hours_required}
        />
      </div>

      {/* Work plan */}
      <WorkPlan items={referral.activities} />
    </article>
  );
};

const MyCaseDashboard = () => {
  // status: "loading" | "success" | "error"
  const [status, setStatus] = useState("loading");
  const [referrals, setReferrals] = useState([]);

  const fetchCase = useCallback(async () => {
    setStatus("loading");
    try {
      const {result, data} = await loadMyCase();
      if (result !== "success") throw new Error("Request was not successful");
      setReferrals(Array.isArray(data?.referrals) ? data.referrals : []);
      setStatus("success");
    } catch (err) {
      console.error("Failed to load case:", err);
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    fetchCase();
  }, [fetchCase]);

  return (
    <div className="min-h-full bg-white pb-10 font-sans">
      {/* Page intro + view-only note */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#191919]">My Work Plan</h2>
        <p className="mt-1 text-base text-slate-600">
          Here is your job training work plan and the hours you have this month.
        </p>
        <div
          className="mt-4 flex items-start gap-3 rounded-2xl border p-4"
          style={{borderColor: "#CBD3F0", backgroundColor: "#EEF1FB"}}
        >
          <Eye
            size={20}
            className="mt-0.5 shrink-0"
            style={{color: DHW_BLUE}}
            aria-hidden="true"
          />
          <p className="text-sm text-slate-700">
            <span className="font-bold" style={{color: DHW_BLUE}}>
              View only.
            </span>{" "}
            You can read your case here. To make a change, talk to your case
            manager.
          </p>
        </div>
      </div>

      {/* Loading */}
      {status === "loading" && (
        <div className="flex min-h-[40vh] items-center justify-center">
          <OrbitLoader />
        </div>
      )}

      {/* Error */}
      {status === "error" && (
        <div className="mx-auto max-w-md rounded-[2rem] border border-rose-100 bg-rose-50 p-8 text-center">
          <AlertCircle
            size={40}
            className="mx-auto mb-3 text-rose-500"
            aria-hidden="true"
          />
          <h3 className="text-lg font-bold text-[#191919]">
            We could not load your case
          </h3>
          <p className="mt-1 text-sm text-slate-600">
            Something went wrong. Please try again. If it keeps happening, call
            your case manager.
          </p>
          <button
            type="button"
            onClick={fetchCase}
            className="mt-5 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-white focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{backgroundColor: DHW_BLUE}}
          >
            <RefreshCw size={16} />
            Try again
          </button>
        </div>
      )}

      {/* Empty */}
      {status === "success" && referrals.length === 0 && (
        <div className="mx-auto max-w-md rounded-[2rem] border border-slate-100 bg-slate-50 p-8 text-center">
          <Inbox
            size={40}
            className="mx-auto mb-3 text-slate-400"
            aria-hidden="true"
          />
          <h3 className="text-lg font-bold text-[#191919]">
            No case details yet
          </h3>
          <p className="mt-1 text-sm text-slate-600">
            We do not have any case information for you right now. Your case
            manager will add it soon.
          </p>
        </div>
      )}

      {/* Success with referrals */}
      {status === "success" && referrals.length > 0 && (
        <div className="space-y-6">
          {referrals.length > 1 && (
            <p
              className="flex items-center gap-2 text-sm font-semibold"
              style={{color: DHW_BLUE}}
            >
              <Clock size={16} aria-hidden="true" />
              You have {referrals.length} work plans. They are all shown below.
            </p>
          )}
          {referrals.map((referral, index) => (
            <ReferralCard
              key={referral.referral_id || index}
              referral={referral}
              index={index}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MyCaseDashboard;
